import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import {
  workspaceMembersQueryOptions,
  workspacesQueryOptions,
} from '@/lib/api/workspaces.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

import { resolveCurrentUserRole } from './workspace-access.js';

/**
 * The caller's role in a workspace, resolved from the registry and the member directory the shell
 * already keeps cached. For the surfaces that are opened from somewhere with no route to hand a role
 * down — the file picker in the composer, the files of an automation.
 *
 * No platform-admin elevation, exactly like the `/artifacts` route. What the role decides here is what
 * to OFFER; the artifact owner's answer stays the authority, and a 403 is handled as a failure like any
 * other. Both reads stay idle while `enabled` is false.
 */
export interface CurrentWorkspaceRoleState {
  role: string | null;
  /** False while any input needed to authorize the caller is loading or failed. */
  ready: boolean;
  /** True when a read needed to authorize the caller failed, as opposed to still loading. */
  failed: boolean;
}

export function useCurrentWorkspaceRoleState(
  workspaceId: string,
  enabled: boolean,
): CurrentWorkspaceRoleState {
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const workspacesQuery = useQuery({ ...workspacesQueryOptions(), enabled });
  const membersQuery = useQuery({
    ...workspaceMembersQueryOptions(workspaceId),
    enabled,
  });
  return {
    role: resolveCurrentUserRole({
      workspace: workspacesQuery.data?.find(
        (candidate) => candidate.id === workspaceId,
      ),
      members: membersQuery.data,
      currentUserId: identity.identity?.userId ?? null,
      isAdmin: false,
    }),
    ready:
      enabled &&
      identity.loaded &&
      workspacesQuery.isSuccess &&
      !workspacesQuery.isFetching &&
      membersQuery.isSuccess &&
      !membersQuery.isFetching,
    failed: workspacesQuery.isError || membersQuery.isError,
  };
}

export function useCurrentWorkspaceRole(
  workspaceId: string,
  enabled: boolean,
): string | null {
  return useCurrentWorkspaceRoleState(workspaceId, enabled).role;
}
